/**
 * Helpers to build human-readable `details` strings for the Activity feed.
 *
 * Rules:
 * - `details` must be human-readable. NEVER include IDs, UUIDs, or raw objects.
 * - For updates / connections, describe WHAT changed (old -> new).
 * - Inserts/deletes usually need no details (the summary covers them); only add
 *   details when the summary alone wouldn't make the action clear.
 */

const isEmpty = (v) =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '');

/**
 * Format a single value for display in a details line.
 * @param {*} value
 * @param {(v:*) => string} [formatter] - optional custom formatter
 * @returns {string|null} display string, or null when the value is empty
 */
const formatValue = (value, formatter, oldObj, newObj) => {
    if (isEmpty(value)) return null;
    if (formatter) {
        const out = formatter(value, oldObj, newObj);
        return isEmpty(out) ? null : String(out);
    }
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value instanceof Date) return value.toLocaleDateString();
    return String(value);
};

// Internal / system fields that should never appear in activity details
const INTERNAL_KEYS = new Set([
    'id', 'createdAt', 'updatedAt', 'organizationID',
    'isDeleted', 'isArchived', 'canceled', 'isProtected',
    'isDynamicRole', 'isAdmin', 'canManage',
    'avatarUpdatedAt', 'avatarStorageKey', 'lastStatusChange',
    'originalValues', 'QuickFilter', 'Method', 'Person', 'Funds',
    'Roles', 'Tasks', 'Permissions', 'People', 'Students', 'Teachers',
    'Classes', 'Requirements', 'Locations', 'EventTasks', 'TaskInstances',
    'VolunteerTeamTasks', 'VolunteerTeamAssigners', 'RolePeople',
    'SmallGroupMembers', 'ContributionFundTransactions',
    'CustomFields', 'Fields', 'HouseholdMembers', 'Phones',
    'Family', 'FamilyMembers', 'Address', 'Leader', 'SmallGroupType',
    'Event', 'EventInstance', 'Fund', 'Batch', 'Calendar',
    'search_vector', 'customFieldValues', 'defaultCustomFieldValues',
    // Recurrence / rrule internals
    'rruleString', 'recurrenceDaysOfMonth', 'recurrenceDaysOfWeek',
    'recurrenceEndDate', 'recurrenceMonths', 'recurrenceWeeksOfMonth',
    'recurringFrequency', 'recurringInterval', 'recurringReferenceDate',
    'includeServiceFlowInExternalCalendar',
    // Computed / aggregate fields
    'pastInstanceCount',
]);

const isScalar = (v) => {
    if (v === null || v === undefined) return true;
    const t = typeof v;
    return t === 'string' || t === 'number' || t === 'boolean';
};

const titleCase = (str) =>
    str
        .replace(/([a-z])([A-Z])/g, '$1 $2')          // insert space: SmallGroup -> Small Group
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')   // handle consecutive caps: URLPath -> URL Path
        .replace(/^./, s => s.toUpperCase());

/**
 * Build human-readable update lines by diffing an old object against a new one.
 * Automatically discovers scalar field changes; explicit `fields` config provides
 * custom labels and formatters for known fields.
 *
 * @param {Object} oldObj - record state BEFORE the update
 * @param {Object} newObj - record state AFTER the update (or the patch)
 * @param {Object<string, {label: string, format?: (v:*) => string}>} fields
 *        config keyed by the object property name. `label` is shown to the user.
 * @returns {string|null} newline-joined "- ..." lines, or null if nothing changed
 */
export const buildUpdateDetails = (oldObj = {}, newObj = {}, fields = {}) => {
    const lines = [];

    // 1. Process explicitly-configured fields (custom labels / formatters)
    for (const key of Object.keys(fields)) {
        const { label, format } = fields[key];
        const oldVal = formatValue(oldObj?.[key], format, oldObj, newObj);
        const newVal = formatValue(newObj?.[key], format, oldObj, newObj);

        if (oldVal === newVal) continue;

        if (oldVal === null) {
            lines.push(`- ${label} was updated to ${newVal}`);
        } else if (newVal === null) {
            lines.push(`- ${label} was cleared`);
        } else {
            lines.push(`- ${label} was updated from ${oldVal} to ${newVal}`);
        }
    }

    // 2. Auto-discover any other scalar fields that changed
    const allKeys = new Set([
        ...Object.keys(oldObj || {}),
        ...Object.keys(newObj || {}),
    ]);

    for (const key of allKeys) {
        if (key in fields) continue;           // already handled above
        if (INTERNAL_KEYS.has(key)) continue;  // skip nested / system keys
        if (key.startsWith('_')) continue;
        if (key.endsWith('ID')) continue;      // skip raw foreign keys (use explicit config if needed)

        const oldRaw = oldObj?.[key];
        const newRaw = newObj?.[key];

        // Skip objects/arrays — those are handled by caller-specific logic
        if (!isScalar(oldRaw) || !isScalar(newRaw)) continue;

        const oldVal = formatValue(oldRaw);
        const newVal = formatValue(newRaw);

        if (oldVal === newVal) continue;

        const label = titleCase(key);
        if (oldVal === null) {
            lines.push(`- ${label} was updated to ${newVal}`);
        } else if (newVal === null) {
            lines.push(`- ${label} was cleared`);
        } else {
            lines.push(`- ${label} was updated from ${oldVal} to ${newVal}`);
        }
    }

    return lines.length ? lines.join('\n') : null;
};

/**
 * Join an arbitrary set of detail lines/strings, dropping empty ones.
 * Useful for combining a scalar-field diff with associative add/remove lines.
 *
 * @param {...(string|null|undefined|Array<string|null|undefined>)} parts
 * @returns {string|null}
 *
 * @example
 * joinDetails(
 *   buildUpdateDetails(old, next, fields),
 *   addedTeams.map(t => `- Added tasks for '${t.name}' Team`),
 *   removedTeams.map(t => `- Removed tasks for '${t.name}' Team`),
 * )
 */
/**
 * Build human-readable custom field diff lines.
 * @param {Object} oldValues - customFieldValues BEFORE update (keyed by field ID)
 * @param {Object} newValues - customFieldValues AFTER update (keyed by field ID)
 * @param {Object<string,string>} nameMap - maps custom field ID → human name
 * @returns {string|null}
 */
export const buildCustomFieldDetails = (oldValues = {}, newValues = {}, nameMap = {}) => {
    const lines = [];
    const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
    for (const key of allKeys) {
        const oldVal = formatValue(oldValues[key]);
        const newVal = formatValue(newValues[key]);
        if (oldVal === newVal) continue;
        const label = nameMap[key] || titleCase(key);
        if (oldVal === null) {
            lines.push(`- ${label} was updated to ${newVal}`);
        } else if (newVal === null) {
            lines.push(`- ${label} was cleared`);
        } else {
            lines.push(`- ${label} was updated from ${oldVal} to ${newVal}`);
        }
    }
    return lines.length ? lines.join('\n') : null;
};

/**
 * Format a camelCase/PascalCase subject type for display.
 * @param {string} subjectType
 * @returns {string}
 */
export const formatSubjectType = (subjectType = '') =>
    subjectType
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/^./, s => s.toUpperCase());

export const joinDetails = (...parts) => {
    const lines = parts.flat(Infinity).filter((l) => !isEmpty(l));
    return lines.length ? lines.join('\n') : null;
};

/**
 * Resolve an activity row to a deep-link route for its subject.
 * Returns null when there is no route (e.g. deleted, or unsupported type).
 *
 * Notes:
 * - Task assign/unassign logs the Task INSTANCE id as subjectID, so it routes
 *   to the instance page rather than the parent task.
 * - SmallGroupSession subjectID is the SmallGroup id (sessions live under it).
 *
 * @param {Object} activity - activity row
 * @returns {string|null}
 */
export const getActivityRoute = (activity) => {
    if (!activity || activity.verb === 'delete') return null;
    const { subjectType, subjectID, verb } = activity;
    if (!subjectID) return null;

    switch (subjectType) {
        case 'Person': return `/people/${subjectID}`;
        case 'Event': return `/events/${subjectID}`;
        case 'EventInstance': return `/events/instances/${subjectID}`;
        case 'Task':
            return verb === 'assign' || verb === 'unassign'
                ? `/tasks/instances/${subjectID}`
                : `/tasks/${subjectID}`;
        case 'SmallGroup': return `/smallgroups/${subjectID}`;
        case 'SmallGroupSession': return `/smallgroups/${subjectID}?tab=sessions`;
        case 'Path': return `/automations/${subjectID}`;
        case 'Family': return `/families/${subjectID}`;
        case 'Role': return `/roles/${subjectID}`;
        case 'TrainingModule': return `/training/${subjectID}`;
        case 'Form': return `/forms/${subjectID}`;
        default: return null;
    }
};
